import React from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import './App.css';

import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { ThemeProvider } from './context/ThemeContext';
import { UIProvider } from './context/UIContext';

import TopBar from './components/common/TopBar';
import Navbar from './components/common/Navbar';
import BlackBar from './components/common/BlackBar';
import Footer from './components/common/Footer';
import ProtectedRoute from './components/common/ProtectedRoute';
import VisualSearch from './components/common/VisualSearch';
import NewsletterPopup from './components/common/NewsletterPopup';
import WhatsAppButton from './components/common/WhatsAppButton';

import HomePage from './pages/HomePage';
import ShopPage from './pages/ShopPage';
import ShopAllPage from './pages/ShopPage'; // new
import ProductPage from './pages/ProductPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import TrackOrderPage from './pages/TrackOrderPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import SearchPage from './pages/SearchPage';
import UserDashboard from './pages/dashboard/UserDashboard';
import ProfilePage from './pages/ProfilePage';
import OrdersPage from './pages/OrdersPage';
import CategoryPage from './pages/CategoryPage';
import OrderConfirmation from './pages/OrderConfirmation';

import SizeChartPage from './pages/SizeChartPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import ShippingPolicyPage from './pages/ShippingPolicyPage';
import ReturnsPage from './pages/ReturnsPage';
import BlogsPage from './pages/BlogsPage';
import OurStoryPage from './pages/OurStoryPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

const LayoutWrapper = ({ children }) => {
  const location = useLocation();
  const isCheckoutPage = location.pathname === '/checkout';

  return (
    <div className="App">
      {!isCheckoutPage && <TopBar />}
      {!isCheckoutPage && <Navbar />}
      {!isCheckoutPage && <BlackBar />}
      {!isCheckoutPage && <VisualSearch />}
      {children}
      {!isCheckoutPage && <Footer />}
      <WhatsAppButton />
    </div>
  );
};

function App() {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AuthProvider>
        <UIProvider>
          <CartProvider>
            <ThemeProvider>
              <LayoutWrapper>
                <NewsletterPopup />
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/shop" element={<ShopPage />} />
                  <Route path="/shop-all" element={<ShopAllPage />} /> {/* new route */}
                  <Route path="/product/:id" element={<ProductPage />} />
                  <Route path="/cart" element={<CartPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/order-confirmation" element={<OrderConfirmation />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/track-order" element={<TrackOrderPage />} />
                  <Route path="/unauthorized" element={<UnauthorizedPage />} />
                  <Route path="/category/:slug" element={<CategoryPage />} />
                  <Route path="/search" element={<SearchPage />} />

                  <Route path="/size-chart" element={<SizeChartPage />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/shipping-policy" element={<ShippingPolicyPage />} />
                  <Route path="/returns" element={<ReturnsPage />} />
                  <Route path="/blogs" element={<BlogsPage />} />
                  <Route path="/our-story" element={<OurStoryPage />} />

                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute allowedRoles={['user', 'admin', 'sub-admin']}>
                        <UserDashboard />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute allowedRoles={['user', 'admin', 'sub-admin']}>
                        <ProfilePage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/orders"
                    element={
                      <ProtectedRoute allowedRoles={['user', 'admin', 'sub-admin']}>
                        <OrdersPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <Navigate to="/dashboard" replace />
                      </ProtectedRoute>
                    }
                  />

                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </LayoutWrapper>
            </ThemeProvider>
          </CartProvider>
        </UIProvider>
      </AuthProvider>
      <Toaster
        position="top-center"
        reverseOrder={false}
        gutter={8}
        containerClassName=""
        containerStyle={{}}
        toastOptions={{
          duration: 3000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#22c55e',
              secondary: '#fff',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </Router>
  );
}

export default App;